import Decimal from 'decimal.js-light';
import { reconcile } from 'solid-js/store';
import { ApiResponse, createAsyncedStore, setting } from '@bz/request';
import { createTimer } from '@solid-primitives/timer';

/** TODO: 和身份无关，可以共享 */
export const [exchangeRate, mountExchangeRate, setExchangeRate] = createAsyncedStore<ApiResponse<'getExchangeRate'>>(
  () => {
    // 每五分钟同步一次汇率
    createTimer(
      () => {
        revalidate(get.getExchangeRate.key);
      },
      1000 * 60 * 5, // 5 minutes,
      setInterval
    );

    return get.getExchangeRate().catch(() => ({}));
  }
);

/** 刷新为实时汇率（用于需要精确汇率的场景，如下单） */
async function refreshWithRealTimeRate() {
  const data = await get.getRealTimeExchangeRate().catch(() => ({}));
  // 使用 reconcile 安全地更新 store，会正确触发响应式更新并清理旧属性
  setExchangeRate(reconcile(data));
  return data;
}

type Option = {
  precision: number;
  target: string;
  padZeros: boolean;
  hasAlias: boolean; // 是否显示别名(加密货币有效)
};

const initCurrency = () => {
  const prefixes = [
    // 产品和设计要求把G改成B
    ['B', 9],
    ['M', 6],
    ['K', 3],
    ['', 1],
    ['m', -3],
    ['μ', -6],
    ['n', -9],
    // ['G', 9],
    // ['M', 6],
    // ['k', 3],
    // ['', 1],
    // ['m', -3],
    // ['μ', -6],
    // ['n', -9],
  ] as [string, number][];
  /** 获取有效精度范围内的科学计数法前缀
   * 使用国际标准的SI科学计数法。
   * https://en.wikipedia.org/wiki/International_System_of_Units
   */
  function getExponential(num: number, precision: number = 6): { num: number; unit: string; exp: number } {
    // 如果数值本身不够大（小于 1000000），不使用科学计数法单位
    const absNum = Math.abs(num);
    if (absNum < 1000000) {
      return { num, exp: 1, unit: '' };
    }

    // 使用 toPrecision 方法来格式化输出，不处理小数部分
    // 使用一个合理的精度值来计算指数，避免 precision=0 导致的问题
    const effectivePrecision = Math.max(precision, 6);
    const formatted = num.toExponential(effectivePrecision);
    const [, exponent] = formatted.split('e').map(Number);
    if (exponent > effectivePrecision) {
      const diff = Math.max(3 - Math.ceil((exponent - effectivePrecision) / 3), 0);
      const [unit, exp] = prefixes[diff];
      return {
        num: exp === 1 ? num : num * 10 ** -exp,
        exp,
        unit,
      };
    } else {
      return { num, exp: 1, unit: '' };
    }
  }

  // 1) 在模块作用域可选地缓存支持的 currency，避免频繁 try/catch
  const SUPPORTED_CURRENCY_SET: Set<string> | null = (() => {
    if (typeof (Intl as any).supportedValuesOf === 'function') {
      try {
        return new Set((Intl as any).supportedValuesOf('currency'));
      } catch {
        return null;
      }
    }
    return null;
  })();

  function isCurrencySupported(code: string, lang = setting.lang): boolean {
    if (!code) return false;
    // 优先用 supportedValuesOf（更快更准确）
    if (SUPPORTED_CURRENCY_SET) return SUPPORTED_CURRENCY_SET.has(code.toUpperCase());
    // 回退：用 try/catch 探针
    try {
      new Intl.NumberFormat(lang, { style: 'currency', currency: code });
      return true;
    } catch {
      return false;
    }
  }

  const getIntl = (
    currency: string,
    precision: number,
    padZeros: boolean = false,
    lang: string = setting.lang
  ): { nf: Intl.NumberFormat; kind: 'currency' | 'decimalFallback' } => {
    if (isCurrencySupported(currency, lang)) {
      // CLDR/引擎支持该币种
      return {
        nf: new Intl.NumberFormat(lang, {
          style: 'currency',
          currency,
          roundingMode: 'trunc',
          minimumFractionDigits: padZeros ? precision : 0,
          maximumFractionDigits: precision,
          numberingSystem: 'latn',
        }),
        kind: 'currency',
      };
    }
    // 不支持：decimal 回退
    return {
      nf: new Intl.NumberFormat(lang, {
        style: 'decimal',
        roundingMode: 'trunc',
        minimumFractionDigits: padZeros ? precision : 0,
        maximumFractionDigits: precision,
        numberingSystem: 'latn',
      }),
      kind: 'decimalFallback',
    };
  };

  // 数字后插入 K/M/B 单位
  function injectKMBIntoParts(parts: Intl.NumberFormatPart[], unit?: string) {
    if (!unit) return parts;

    // 找到数字的最后一个部分（fraction 优先，没有则 integer）
    let lastNumberIdx = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i].type === 'fraction' || parts[i].type === 'integer') {
        lastNumberIdx = i;
        break;
      }
    }
    if (lastNumberIdx === -1) return parts;

    // 在数字后面插入一个 literal（可加窄不换行空格 \u202F 再接单位）
    const newParts = parts.slice();
    newParts.splice(lastNumberIdx + 1, 0, {
      type: 'literal',
      value: '\u202F' + unit, // 数字紧跟单位：123 K
    });
    return newParts;
  }

  /**
   * 格式化货币金额
   * @param currencyCode 货币代码
   * @param amount 金额
   * @param precision 精度（小数位数）
   * @param padZeros 是否补零到指定精度，默认 false
   *   - true: 不足精度位数时补零（如 1.23 → 1.230000，精度为6）
   *   - false: 不补零，按实际小数位显示（如 1.23 → 1.23）
   *   - 超出精度时都会向下取整到指定精度
   */
  const format = (
    currencyCode: string,
    amount: Decimal,
    precision: number,
    padZeros: boolean = false,
    hasAlias: boolean = true
  ) => {
    const { num, unit } = getExponential(amount.toNumber(), precision);
    const intlRes = getIntl(currencyCode, precision, padZeros);
    const { nf, kind } = intlRes;

    const fastFormat = () => nf.format(num);

    if (unit) {
      try {
        const parts = nf.formatToParts(num); // ← 修复：用缩放后的 num
        const newParts = injectKMBIntoParts(parts, unit);
        const base = newParts.map((p) => p.value).join('');
        // 若 fallback 为 decimal，需要在显示后追加 currencyCode（保持你的需求）
        return kind === 'decimalFallback' && hasAlias !== false ? `${base} ${currencyCode}` : base;
      } catch {
        // formatToParts 某些古老引擎可能报错，兜底
        const base = fastFormat();
        return kind === 'decimalFallback' && hasAlias !== false
          ? `${base} ${unit} ${currencyCode}`
          : `${base}\u202F${unit}`;
      }
    }

    // 无 K/M/B：正常输出；若 fallback 为 decimal，再追加 currencyCode
    const base = fastFormat();
    return kind === 'decimalFallback' && hasAlias !== false ? `${base} ${currencyCode}` : base;
  };

  return {
    get currencies() {
      return Object.values(exchangeRate);
    },

    getCurrencySymbol(currencyCode: string): string {
      return (
        getIntl(currencyCode, 2, false)
          .nf.formatToParts(1)
          .find((p) => p.type === 'currency')?.value || currencyCode
      );
    },

    /**
     * 汇率转换函数
     * @param amount 要转换的金额
     * @param source 源币种代码
     * @param target 目标币种代码，默认为USD
     * @returns 转换后的金额
     */
    convertCurrency(amount: Decimal, source: string, target: string = 'USD') {
      // 如果源币种和目标币种相同，直接返回原金额
      if (source === target || amount.eq(0)) {
        return amount;
      }

      const sourceCurrency = exchangeRate[source];
      const targetCurrency = exchangeRate[target];

      // 检查币种是否存在
      if (!sourceCurrency || !targetCurrency) {
        console.warn(
          `Currency not found: ${!sourceCurrency ? `source=${source}` : ''}, ${!targetCurrency ? `target=${target}` : ''}`
        );
        return amount;
      }

      // 汇率转换逻辑：
      // 1. 先将源币种转换为USD：amount * sourceCurrency.sellPrice
      // 2. 再将USD转换为目标币种：usdAmount / targetCurrency.sellPrice
      const usdAmount = amount.mul(sourceCurrency.sellPrice);
      const convertedAmount = usdAmount.div(targetCurrency.sellPrice === '0' ? 1 : targetCurrency.sellPrice);

      return convertedAmount;
    },
    /**
     * 将特定币种转换为本地货币，并使用Intl格式化成带符号的字符串 (例如 "$20.00")。
     *
     * @param {Decimal} amount - 原始金额。
     * @param {string} [name='USDFIAT'] - 原始货币名称，默认为 "USDFIAT"。
     * @param {number | Object} [optionOrPrecision] - 转换选项或精度。
     *   如果传入数字，则作为精度使用。否则，使用包含以下属性的对象：
     *   @param {number} [optionOrPrecision.precision=7] - 精度，影响输出的长度，并尽可能限制在该长度范围内。
     *   @param {string} [optionOrPrecision.target] - 目标货币，不传时默认使用 `localCurrencyName`（如果已开启）。
     *   @param {boolean} [optionOrPrecision.hasAlias=false] - 是否显示货币的别名（仅在目标货币为数字货币时有效）。
     */
    printCurrency(
      amount: Decimal | number | string,
      currencyCode: string = 'USD',
      optionOrPrecision?: number | Partial<Option>
    ) {
      amount = new Decimal(amount);

      const DEFAULT_OPTION: Option = {
        precision: 6,
        target: walletSettings.showCurrency ? walletSettings.showCurrency : currencyCode,
        padZeros: false,
        hasAlias: true,
      };
      const option: Option = Object.assign(
        {
          ...DEFAULT_OPTION,
        },
        typeof optionOrPrecision === 'number' ? { precision: optionOrPrecision } : optionOrPrecision
      );

      // convertCurrency 函数已经处理了金额转换逻辑，此处直接使用转换后的金额进行格式化。
      const convertedAmount = this.convertCurrency(amount, currencyCode, option.target);
      return format(option.target, convertedAmount, option.precision, option.padZeros, option.hasAlias);
    },
  };
};

const currency = initCurrency();

const initialWalletSettings = {
  showCurrency: 'USD',
  cryptoShowFullCurrency: 2,
};
export const [walletSettings, mountWalletSettings] = createAsyncedStore<ApiResponse<'getSetting'>>(async () => {
  return (await get.getSetting({ accountType: 'BALANCE' }).catch(() => undefined)) || initialWalletSettings;
});

export { currency, refreshWithRealTimeRate };
